/**
 * Simple chunked upload for large files
 * Handles large file uploads (20MB+) with proper chunking
 */

import axios from 'axios';
import { envApiUrl } from '../fetchHooks/envApiUrl';

const CHUNK_SIZE = 10 * 1024 * 1024; // 10MB chunks for better efficiency
const MAX_RETRIES = 3;

/**
 * Calculate and log upload speed with smooth progress
 */
function logSpeedStats(completedChunks, totalChunks, chunkSize, startTime, fileSize) {
  const elapsedTime = (Date.now() - startTime) / 1000;
  const uploadedBytes = Math.min(completedChunks * chunkSize, fileSize);
  const currentSpeed = uploadedBytes / elapsedTime / 1024; // KB/s
  const progress = Math.min((uploadedBytes / fileSize) * 100, 100);

  console.log(
    `📊 [SPEED] ${Math.round(currentSpeed)} KB/s (${Math.round(progress)}% complete) - Chunk ${completedChunks}/${totalChunks}`,
  );

  return { currentSpeed, progress, uploadedBytes };
}

/**
 * Upload a single chunk with retry logic
 */
async function uploadChunkWithRetry(
  chunkIndex,
  file,
  uploadUrl,
  headers,
  onProgress,
  fileId,
  totalChunks,
  credentials = 'include',
) {
  const start = chunkIndex * CHUNK_SIZE;
  const end = Math.min(start + CHUNK_SIZE, file.size);
  const chunk = file.slice(start, end);

  console.log(`📦 [CHUNK] Uploading chunk ${chunkIndex + 1}/${totalChunks} (${chunk.size} bytes)`);

  const formData = new FormData();
  formData.append('chunk', chunk);
  formData.append('chunkIndex', chunkIndex.toString());
  formData.append('totalChunks', totalChunks.toString());
  formData.append('fileId', fileId);
  formData.append('fileName', file.name);
  formData.append('fileSize', file.size.toString());

  let retries = 0;
  while (retries < MAX_RETRIES) {
    try {
      const response = await fetch(uploadUrl, {
        method: 'POST',
        headers: {
          ...headers,
        },
        credentials: credentials, // Include session cookies for authentication
        body: formData,
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error(`❌ [CHUNK] HTTP ${response.status}: ${errorText}`);
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const result = await response.json();

      if (result.success) {
        console.log(`✅ [CHUNK] Chunk ${chunkIndex + 1} uploaded successfully`);
        return { success: true, chunkIndex, chunkSize: chunk.size };
      } else {
        throw new Error(result.message || 'Upload failed');
      }
    } catch (error) {
      retries++;
      console.warn(`❌ [CHUNK] Chunk ${chunkIndex + 1} failed (attempt ${retries}/${MAX_RETRIES}):`, error);

      if (retries >= MAX_RETRIES) {
        throw new Error(`Failed to upload chunk ${chunkIndex + 1} after ${MAX_RETRIES} attempts: ${error.message}`);
      }

      // Wait before retry
      await new Promise((resolve) => setTimeout(resolve, 1000 * retries));
    }
  }
}

/**
 * Upload a large file in chunks
 * @param {File} file - The file to upload
 * @param {string} uploadUrl - Upload endpoint URL
 * @param {Object} options - Upload options
 * @returns {Promise} - Upload promise
 */
export async function uploadLargeFile(file, uploadUrl, options = {}) {
  const { onProgress = () => {}, headers = {}, credentials = 'include' } = options;

  // Fetch CSRF token once for the whole upload
  let csrfToken = undefined;
  try {
    const csrfResponse = await axios.get(envApiUrl + '/auth/csrf-token', { withCredentials: true });
    csrfToken = csrfResponse.data?.csrfToken;
  } catch (e) {
    console.warn('Could not fetch CSRF token for large upload:', e);
  }

  const mergedHeaders = {
    ...(csrfToken ? { 'x-csrf-token': csrfToken } : {}),
    ...headers,
  };

  const totalChunks = Math.ceil(file.size / CHUNK_SIZE);
  const fileId = `${file.name}-${Date.now()}`;
  const startTime = Date.now();

  console.log(`🚀 [LARGE FILE] Starting parallel chunked upload: ${file.name} (${totalChunks} chunks, ALL parallel)`);

  // Initialize progress
  onProgress(0, file.size, 0);

  try {
    // Upload ALL chunks in parallel for maximum speed
    const uploadPromises = [];
    const completedChunks = { count: 0 };

    console.log(`🚀 [PARALLEL] Starting ${totalChunks} parallel uploads...`);

    for (let chunkIndex = 0; chunkIndex < totalChunks; chunkIndex++) {
      const uploadPromise = uploadChunkWithRetry(
        chunkIndex,
        file,
        uploadUrl,
        mergedHeaders,
        onProgress,
        fileId,
        totalChunks,
        credentials,
      ).then((result) => {
        completedChunks.count++;
        const { currentSpeed, progress, uploadedBytes } = logSpeedStats(
          completedChunks.count,
          totalChunks,
          CHUNK_SIZE,
          startTime,
          file.size,
        );
        onProgress(uploadedBytes, file.size, progress / 100);
        return result;
      });

      uploadPromises.push(uploadPromise);
    }

    // Wait for ALL chunks to complete in parallel
    console.log(`⏳ [PARALLEL] Waiting for ${totalChunks} chunks to complete...`);
    await Promise.all(uploadPromises);

    const totalTime = (Date.now() - startTime) / 1000;
    const avgSpeed = file.size / totalTime / 1024; // KB/s
    const speedMBps = avgSpeed / 1024; // MB/s

    console.log(`✅ [LARGE FILE] Upload completed: ${file.name}`);
    console.log(
      `📊 [FINAL SPEED] ${Math.round(avgSpeed)} KB/s (${speedMBps.toFixed(2)} MB/s) in ${totalTime.toFixed(1)}s`,
    );
    console.log(`📈 [IMPROVEMENT] Parallel uploads: ${totalChunks} chunks simultaneously`);
    return { success: true, uploadedBytes: file.size, fileId };
  } catch (error) {
    console.error('❌ [LARGE FILE] Upload failed:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Check if a file should use chunked upload
 * @param {File} file - The file to check
 * @returns {boolean} - True if chunked upload is recommended
 */
export function shouldUseChunkedUpload(file) {
  const sizeInMB = file.size / (1024 * 1024);
  return sizeInMB > 20; // Use chunked upload for files larger than 20MB
}
